/* assets/product-browser.js */

(function () {
  'use strict';

  // ─── Locale ──────────────────────────────────────────────────────────────────

  function getLocale() {
    return String(window.PB_locale || document.documentElement.lang || 'en').toLowerCase();
  }

  function isFrench() {
    return getLocale().startsWith('fr');
  }

  function t(key, vars = {}) {
    const dict = {
      en: {
        in_stock:         'In stock',
        sold_out:         'Sold out',
        bundle_line:      'Bundle: {{count}} of 3 products',
        add_bundle_error: 'Could not add bundle to cart.',
        empty_selection:  'Select a product',
        loading:          'Loading products...',
      },
      fr: {
        in_stock:         'En stock',
        sold_out:         'Rupture de stock',
        bundle_line:      'Bundle : {{count}} sur 3 produits',
        add_bundle_error: "Impossible d'ajouter le bundle au panier.",
        empty_selection:  'Sélectionnez un produit',
        loading:          'Chargement des produits...',
      },
    };

    const locale = isFrench() ? 'fr' : 'en';
    let str = dict[locale]?.[key] ?? dict.en[key] ?? key;
    for (const [k, v] of Object.entries(vars)) str = str.replace(`{{${k}}}`, v);
    return str;
  }

  // ─── Money ───────────────────────────────────────────────────────────────────

  function formatMoney(cents) {
    const fmt   = window.PB_money_format || '${{amount}}';
    const c     = Number(cents) || 0;
    const amt   = (c / 100).toFixed(2);
    const amtND = String(Math.round(c / 100));

    return fmt
      .replace('{{amount}}',                                  amt)
      .replace('{{amount_no_decimals}}',                      amtND)
      .replace('{{amount_with_comma_separator}}',             amt.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', amtND.replace('.', ','));
  }

  function setMoney(el, cents, prefix = '') {
    if (el) el.innerHTML = prefix + formatMoney(Number(cents) || 0);
  }

  // ─── Utilities ───────────────────────────────────────────────────────────────

  function uid() {
    return Math.random().toString(16).slice(2) + Date.now().toString(16);
  }

  function normalize(v) {
    return String(v || '').trim().toLowerCase();
  }

  function splitCsv(value) {
    return String(value || '').split(',').map(normalize).filter(Boolean);
  }

  function hasValue(csv, value) {
    if (!value) return true;
    return splitCsv(csv).includes(normalize(value));
  }

  // ─── Parse price from Shopify REST (comes as "10.00" string) ─────────────────

  function parsePrice(raw) {
    return Math.round(parseFloat(raw) * 100) || 0;
  }

  // ─── Bundle discount ─────────────────────────────────────────────────────────

  function discountPctForBundle(items) {
    if (items.length >= 3) return 0.15;
    const hasTop    = items.some((it) => hasValue(it.category, 'tops'));
    const hasBottom = items.some((it) => hasValue(it.category, 'bottoms'));
    if (hasTop && hasBottom) return 0.10;
    return 0;
  }

  // ─── Fetch all products via REST (no 50-product section limit) ───────────────

  async function fetchAllProducts(collectionHandle) {
    let page = 1;
    let all  = [];

    while (true) {
      const res  = await fetch(
        `/collections/${collectionHandle}/products.json?limit=250&page=${page}`
      );
      if (!res.ok) break;

      const data     = await res.json();
      const products = data.products || [];

      all = all.concat(products);
      if (products.length < 250) break;
      page++;
    }

    return all;
  }

  // ─── Build bike_type string from tag array ────────────────────────────────────

  function bikeTypeFromTags(tags) {
    const types = [];
    if (tags.some((t) => t.includes('road')))   types.push('Road');
    if (tags.some((t) => t.includes('mtb')))    types.push('Mountain');
    if (tags.some((t) => t.includes('gravel'))) types.push('Gravel');
    return types.length ? types.join(',') : 'Road,Mountain,Gravel';
  }

  // ─── Build category string from tag array ────────────────────────────────────

  function categoryFromTags(tags) {
    const cats = [];
    if (tags.includes('tops'))    cats.push('tops');
    if (tags.includes('bottoms')) cats.push('bottoms');
    return {
      category:        cats.length ? cats.join(',') : 'tops',
      primary_category: cats[0] || 'tops',
    };
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function init(root) {
    const sectionId        = root.dataset.sectionId  || 'default';
    const collectionHandle = root.dataset.collection || '';
    const STORAGE_KEY      = `pb_bundle_${sectionId}`;

    // DOM refs
    const grid        = root.querySelector('[data-grid]');
    const bikeFilters = Array.from(root.querySelectorAll('[data-bike-filter-option]'));

    const hero0    = root.querySelector('[data-hero="0"]');
    const hero1    = root.querySelector('[data-hero="1"]');
    const heroPrev = root.querySelector('[data-hero-prev]');
    const heroNext = root.querySelector('[data-hero-next]');

    const viewLink     = root.querySelector('[data-view]');
    const titleEl      = root.querySelector('[data-title]');
    const priceEl      = root.querySelector('[data-price]');
    const variantsWrap = root.querySelector('[data-variants]');
    const stockEl      = root.querySelector('[data-stock]');
    const selectBtn    = root.querySelector('[data-select]');

    const ratingWrap  = root.querySelector('[data-rating]');
    const ratingStars = root.querySelector('[data-rating-stars]');
    const ratingText  = root.querySelector('[data-rating-text]');

    const featuresWrap = root.querySelector('[data-features]');
    const featuresList = root.querySelector('[data-features-list]');

    const bundleLine       = root.querySelector('[data-bundle-line]');
    const bundleThumbs     = root.querySelector('[data-bundle-thumbs]');
    const bundleTotals     = root.querySelector('[data-bundle-totals]');
    const bundleSubtotalEl = root.querySelector('[data-bundle-subtotal]');
    const bundleSavePctEl  = root.querySelector('[data-bundle-save-pct]');
    const bundleSaveEl     = root.querySelector('[data-bundle-save]');
    const bundleTotalEl    = root.querySelector('[data-bundle-total]');
    const bundleAddBtn     = root.querySelector('[data-bundle-add]');
    const bundleClearBtn   = root.querySelector('[data-bundle-clear]');

    // Live thumb list — populated after fetch
    let thumbs = [];

    // State
    let activeCat       = '';
    let activeProduct   = null;
    let activeVariant   = null;
    let activeHeroIndex = 0;
    let hasSecondHero   = false;
    let bundleId        = null;
    let bundleItems     = [];

    // ── Helpers ────────────────────────────────────────────────────────────────

    function getSelectedBikeType() {
      const active = root.querySelector('[data-bike-filter-option].is-active');
      return active ? normalize(active.dataset.bikeType) : '';
    }

    function matchesBike(productBikeType, selectedBikeType) {
      if (!selectedBikeType) return true;
      return hasValue(productBikeType, selectedBikeType);
    }

    function matchesCategory(productCategories, selectedCategory) {
      if (!selectedCategory) return true;
      return hasValue(productCategories, selectedCategory);
    }

    function getProduct(handle) {
      const node = root.querySelector(`script[data-product="${handle}"]`);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        console.error('[ProductBrowser] Invalid product JSON for', handle, e);
        return null;
      }
    }

    // ── Persistence ────────────────────────────────────────────────────────────

    function saveBundle() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ bundleId, items: bundleItems }));
      } catch (_) {}
    }

    function loadBundle() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const data = JSON.parse(raw);
        if (data?.bundleId && Array.isArray(data.items)) {
          bundleId    = data.bundleId;
          bundleItems = data.items;
        }
      } catch (_) {}
    }

    // ── Filtering ──────────────────────────────────────────────────────────────

    function getVisibleThumbs() {
      const selectedBike = getSelectedBikeType();
      return thumbs.filter((th) =>
        matchesCategory(th.dataset.cat, activeCat) &&
        matchesBike(th.dataset.bikeType, selectedBike)
      );
    }

    function applyFilters({ preserveSelection = false } = {}) {
      const selectedBike = getSelectedBikeType();

      thumbs.forEach((th) => {
        const show = matchesCategory(th.dataset.cat, activeCat) &&
                     matchesBike(th.dataset.bikeType, selectedBike);
        th.style.display = show ? '' : 'none';
        if (!show) th.classList.remove('is-active');
      });

      const visible = getVisibleThumbs();
      if (!visible.length) { clearProductPanel(); return; }

      if (preserveSelection && activeProduct &&
          visible.some((th) => th.dataset.handle === activeProduct.handle)) {
        thumbs.forEach((t) => t.classList.remove('is-active'));
        visible.find((th) => th.dataset.handle === activeProduct.handle)
               ?.classList.add('is-active');
        return;
      }

      visible[0]?.click();
    }

    // ── Product panel ──────────────────────────────────────────────────────────

    function clearProductPanel() {
      activeProduct = null; activeVariant = null;
      activeHeroIndex = 0;  hasSecondHero = false;

      thumbs.forEach((t) => t.classList.remove('is-active'));

      if (viewLink)     { viewLink.href = '#'; viewLink.style.display = 'none'; }
      if (titleEl)        titleEl.textContent       = t('empty_selection');
      if (priceEl)        priceEl.textContent        = '';
      if (stockEl)        stockEl.textContent        = '';
      if (selectBtn)      selectBtn.disabled         = true;
      if (variantsWrap) { variantsWrap.innerHTML     = ''; variantsWrap.style.display = 'none'; }
      if (featuresWrap)   featuresWrap.style.display  = 'none';
      if (ratingWrap)     ratingWrap.style.display    = 'none';
      if (ratingStars)    ratingStars.innerHTML        = '';
      if (ratingText)     ratingText.textContent       = '';

      if (hero0) { hero0.removeAttribute('src'); hero0.alt = ''; hero0.classList.add('is-active'); }
      if (hero1) { hero1.removeAttribute('src'); hero1.alt = ''; hero1.classList.remove('is-active'); hero1.style.display = 'none'; }
    }

    function renderProduct(product) {
      activeProduct = product;

      if (viewLink) {
        viewLink.href          = product.url || '#';
        viewLink.style.display = product.url ? '' : 'none';
      }

      if (titleEl) titleEl.textContent = product.title || '';

      const imgs = product.images || [];
      const imgA = imgs[0] || product.featured_image || '';
      const imgB = imgs[1] || null;

      activeHeroIndex = 0;
      hasSecondHero   = !!imgB;

      if (hero0) { hero0.src = imgA; hero0.alt = product.title || ''; hero0.classList.add('is-active'); }
      if (hero1) {
        hero1.src           = imgB || imgA;
        hero1.alt           = product.title || '';
        hero1.style.display = imgB ? '' : 'none';
        hero1.classList.remove('is-active');
      }

      if (ratingWrap) {
        const val   = Number(product.rating_value) || 0;
        const count = Number(product.rating_count) || 0;
        if (val && count) {
          ratingWrap.style.display = '';
          if (ratingText)  ratingText.textContent = `${val.toFixed(1)} (${count})`;
          if (ratingStars) {
            const r = Math.round(val);
            ratingStars.innerHTML = '★'.repeat(r) + '☆'.repeat(Math.max(0, 5 - r));
          }
        } else {
          ratingWrap.style.display = 'none';
          if (ratingStars) ratingStars.innerHTML  = '';
          if (ratingText)  ratingText.textContent = '';
        }
      }

      renderVariants(product);
      renderFeatures(product);
    }

    function renderFeatures(product) {
      if (!featuresWrap || !featuresList) return;
      featuresList.innerHTML = '';
      if (product.features?.length) {
        product.features.forEach((f) => {
          const li = document.createElement('li');
          li.textContent = f;
          featuresList.appendChild(li);
        });
        featuresWrap.style.display = '';
      } else {
        featuresWrap.style.display = 'none';
      }
    }

    function renderVariants(product) {
      if (!variantsWrap) return;
      variantsWrap.innerHTML = '';

      const variants = product.variants || [];
      if (!variants.length) { variantsWrap.style.display = 'none'; return; }

      const base    = variants.find((v) => v.available) || variants[0];
      activeVariant = base;
      setMoney(priceEl, base.price);
      if (stockEl)   stockEl.textContent = base.available ? t('in_stock') : t('sold_out');
      if (selectBtn) selectBtn.disabled  = !base.available;

      // Deduplicate by option2 → option1 → title
      const seen = new Map();
      variants.forEach((v) => {
        const key = v.option2 || v.option1 || v.title || 'Default';
        if (!seen.has(key)) seen.set(key, v);
      });

      if (seen.size <= 1) { variantsWrap.style.display = 'none'; return; }

      variantsWrap.style.display = '';

      seen.forEach((variant, label) => {
        const btn = document.createElement('button');
        btn.type        = 'button';
        btn.className   = 'ProductBrowser__variantBtn';
        btn.textContent = label;
        if (!variant.available) { btn.classList.add('is-disabled'); btn.disabled = true; }
        if (variant.id === base.id) btn.classList.add('is-active');

        btn.addEventListener('click', () => {
          if (!variant.available) return;
          activeVariant = variant;
          variantsWrap.querySelectorAll('.ProductBrowser__variantBtn')
            .forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          setMoney(priceEl, variant.price);
          if (stockEl)   stockEl.textContent = variant.available ? t('in_stock') : t('sold_out');
          if (selectBtn) selectBtn.disabled  = !variant.available;
        });

        variantsWrap.appendChild(btn);
      });
    }

    // ── Bundle ─────────────────────────────────────────────────────────────────

    function addCurrentToBundle() {
      if (!activeProduct || !activeVariant?.available) return;
      if (bundleItems.length >= 3) return;
      if (bundleItems.some((it) => it.variant_id === activeVariant.id)) return;

      if (!bundleId) bundleId = `bundle_${uid()}`;

      bundleItems.push({
        variant_id: activeVariant.id,
        price:      activeVariant.price,
        title:      activeProduct.title,
        image:      activeProduct.featured_image,
        category:   activeProduct.primary_category || activeProduct.category || activeCat || 'tops',
      });

      saveBundle();
      renderBundle();
    }

    function renderBundle() {
      const count = bundleItems.length;

      if (bundleLine) bundleLine.textContent = t('bundle_line', { count: Math.min(count, 3) });

      if (bundleThumbs) {
        bundleThumbs.innerHTML = '';
        bundleItems.forEach((item, idx) => {
          const wrap = document.createElement('div');
          wrap.className = 'BundleSummary__item';

          const img = document.createElement('img');
          img.src = item.image; img.alt = item.title || '';
          wrap.appendChild(img);

          const rm = document.createElement('button');
          rm.type = 'button'; rm.textContent = '×'; rm.className = 'BundleSummary__remove';
          rm.onclick = () => {
            bundleItems.splice(idx, 1);
            if (!bundleItems.length) bundleId = null;
            saveBundle(); renderBundle();
          };
          wrap.appendChild(rm);
          bundleThumbs.appendChild(wrap);
        });
      }

      const subtotal = bundleItems.reduce((s, it) => s + it.price, 0);
      const pct      = discountPctForBundle(bundleItems);
      const discount = Math.round(subtotal * pct);
      const total    = subtotal - discount;

      if (count) {
        if (bundleTotals) bundleTotals.style.display = '';
        setMoney(bundleSubtotalEl, subtotal);
        if (bundleSavePctEl) bundleSavePctEl.textContent = `${Math.round(pct * 100)}%`;
        setMoney(bundleSaveEl, discount, '- ');
        setMoney(bundleTotalEl, total);
      } else {
        if (bundleTotals) bundleTotals.style.display = 'none';
      }

      const canAdd = count >= 3 ||
        (bundleItems.some((it) => hasValue(it.category, 'tops')) &&
         bundleItems.some((it) => hasValue(it.category, 'bottoms')));

      if (bundleAddBtn)   bundleAddBtn.disabled        = !canAdd;
      if (bundleClearBtn) bundleClearBtn.style.display = count ? '' : 'none';
    }

    async function addBundleToCart(items, id) {
      const payload = items.map((it, i) => ({
        id:       it.variant_id,
        quantity: 1,
        properties: { _bundle_id: id, _bundle_index: i + 1, _bundle_size: items.length },
      }));

      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items: payload }),
      });

      if (!res.ok) throw new Error(await res.text());
      return res.json();
    }

    // ── Build & register a single thumb from a REST product object ─────────────

    function buildThumb(p) {
      const tags        = (p.tags || []).map((tg) => normalize(tg));
      const bikeType    = bikeTypeFromTags(tags);
      const { category, primary_category } = categoryFromTags(tags);

      const featuredImage = p.images?.[0]?.src || '';
      const image1        = p.images?.[1]?.src || featuredImage;

      // Thumbnail button
      const btn = document.createElement('button');
      btn.className        = 'ProductBrowser__thumb';
      btn.type             = 'button';
      btn.dataset.handle   = p.handle;
      btn.dataset.cat      = category;
      btn.dataset.bikeType = bikeType;
      btn.setAttribute('aria-label', isFrench() ? `Sélectionner ${p.title}` : `Select ${p.title}`);

      if (featuredImage) {
        const img   = document.createElement('img');
        img.src     = featuredImage;
        img.alt     = p.title || '';
        img.loading = 'lazy';
        btn.appendChild(img);
      }

      // Inline JSON for getProduct()
      const script             = document.createElement('script');
      script.type              = 'application/json';
      script.dataset.product   = p.handle;
      script.textContent       = JSON.stringify({
        id:               p.id,
        handle:           p.handle,
        title:            p.title,
        url:              `/products/${p.handle}`,
        featured_image:   featuredImage,
        bike_type:        bikeType,
        category,
        primary_category,
        images:           [featuredImage, image1].filter(Boolean),
        options:          (p.options || []).map((o) => o.name),
        variants:         (p.variants || []).map((v) => ({
          id:        v.id,
          title:     v.title,
          available: v.available,
          price:     parsePrice(v.price),
          option1:   v.option1,
          option2:   v.option2,
          option3:   v.option3,
          image:     v.featured_image?.src || featuredImage,
        })),
        fabric:       '',
        features:     [],
        rating_value: 0,
        rating_max:   5,
        rating_count: 0,
      });

      btn.addEventListener('click', () => {
        thumbs.forEach((t) => t.classList.remove('is-active'));
        btn.classList.add('is-active');
        const product = getProduct(p.handle);
        if (product) renderProduct(product);
      });

      grid.appendChild(btn);
      grid.appendChild(script);

      return btn;
    }

    // ── Events ─────────────────────────────────────────────────────────────────

    bikeFilters.forEach((btn) => {
      btn.addEventListener('click', () => {
        const wasActive = btn.classList.contains('is-active');
        bikeFilters.forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        if (!wasActive) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
        }
        applyFilters();
      });
    });

    selectBtn?.addEventListener('click', addCurrentToBundle);

    if (bundleClearBtn) {
      bundleClearBtn.onclick = () => {
        bundleItems = []; bundleId = null;
        saveBundle(); renderBundle();
      };
    }

    if (bundleAddBtn) {
      bundleAddBtn.onclick = async () => {
        const count     = bundleItems.length;
        const hasTop    = bundleItems.some((it) => hasValue(it.category, 'tops'));
        const hasBottom = bundleItems.some((it) => hasValue(it.category, 'bottoms'));
        if (!(count >= 3 || (hasTop && hasBottom))) return;

        try {
          await addBundleToCart(bundleItems, bundleId);
          bundleItems = []; bundleId = null;
          saveBundle(); renderBundle();
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:open'));
        } catch (e) {
          console.error('[ProductBrowser] cart error', e);
          alert(t('add_bundle_error'));
        }
      };
    }

    function toggleHero() {
      if (!hasSecondHero) return;
      activeHeroIndex = activeHeroIndex === 0 ? 1 : 0;
      hero0?.classList.toggle('is-active', activeHeroIndex === 0);
      hero1?.classList.toggle('is-active', activeHeroIndex === 1);
    }

    heroPrev?.addEventListener('click', toggleHero);
    heroNext?.addEventListener('click', toggleHero);

    // ── Boot: sync aria, fetch products, render ────────────────────────────────

    bikeFilters.forEach((b) => {
      b.setAttribute('aria-selected', b.classList.contains('is-active') ? 'true' : 'false');
    });

    if (titleEl) titleEl.textContent = t('loading');

    loadBundle();
    renderBundle();

    fetchAllProducts(collectionHandle).then((products) => {
      if (!products.length) { clearProductPanel(); return; }

      products.forEach((p) => {
        const btn = buildThumb(p);
        thumbs.push(btn);
      });

      applyFilters();
    }).catch((err) => {
      console.error('[ProductBrowser] fetch error', err);
      clearProductPanel();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.ProductBrowser').forEach(init);
  });

})();